import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Typography,
  Box,
  Chip,
  Alert,
  MenuItem,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Autocomplete,
  Avatar,
  Skeleton,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Build as BuildIcon,
  Visibility as VisibilityIcon,
  Directions as DirectionsIcon,
  Search as SearchIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { cantieriService, clientiService } from '../services/api';
import { iniziali } from '../utils/formato';

// Componente StatBox per le statistiche in header
const StatBox = ({ icon: Icon, value, label, color }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      p: 2,
      bgcolor: 'rgba(255,255,255,0.1)',
      borderRadius: 2,
      flex: 1,
      minWidth: { xs: '45%', sm: 'auto' },
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 1.5,
        bgcolor: color || 'rgba(255,255,255,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ color: 'white', fontSize: 22 }} />
    </Box>
    <Box>
      <Typography variant="h5" sx={{ color: 'white', fontWeight: 800, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

const Cantieri = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [cantieri, setCantieri] = useState([]);
  const [filteredCantieri, setFilteredCantieri] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentCantiere, setCurrentCantiere] = useState(null);
  const [cantiereToDelete, setCantiereToDelete] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    codice: '',
    nome: '',
    cliente_id: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    referente_cantiere: '',
    telefono_referente: '',
    email_referente: '',
    stato: 'attivo',
    data_inizio: null,
    data_fine_prevista: null,
    note: '',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const filtered = cantieri.filter(cantiere => {
      const searchLower = searchTerm.toLowerCase();
      const clienteName = clienti.find(c => c.id === cantiere.cliente_id)?.ragione_sociale || '';
      return (
        cantiere.codice?.toLowerCase().includes(searchLower) ||
        cantiere.nome?.toLowerCase().includes(searchLower) ||
        cantiere.citta?.toLowerCase().includes(searchLower) ||
        cantiere.indirizzo?.toLowerCase().includes(searchLower) ||
        clienteName.toLowerCase().includes(searchLower)
      );
    });
    setFilteredCantieri(filtered);
    setPage(0);
  }, [searchTerm, cantieri, clienti]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedCantieri = filteredCantieri.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const loadData = async () => {
    try {
      setLoading(true);
      const [cantieriRes, clientiRes] = await Promise.all([
        cantieriService.getAll(),
        clientiService.getAll(),
      ]);
      setCantieri(cantieriRes.data);
      setFilteredCantieri(cantieriRes.data);
      setClienti(clientiRes.data);
    } catch (error) {
      setError('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (cantiere = null) => {
    if (cantiere) {
      setCurrentCantiere(cantiere);
      setFormData({
        ...cantiere,
        data_inizio: cantiere.data_inizio ? dayjs(cantiere.data_inizio) : null,
        data_fine_prevista: cantiere.data_fine_prevista ? dayjs(cantiere.data_fine_prevista) : null,
      });
    } else {
      setCurrentCantiere(null);
      setFormData({
        codice: '',
        nome: '',
        cliente_id: '',
        indirizzo: '',
        citta: '',
        cap: '',
        provincia: '',
        referente_cantiere: '',
        telefono_referente: '',
        email_referente: '',
        stato: 'attivo',
        data_inizio: null,
        data_fine_prevista: null,
        note: '',
      });
    }
    setDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentCantiere(null);
    setError('');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDateChange = (field) => (value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleSubmit = async () => {
    try {
      const dataToSend = {
        ...formData,
        data_inizio: formData.data_inizio ? formData.data_inizio.format('YYYY-MM-DD') : null,
        data_fine_prevista: formData.data_fine_prevista ? formData.data_fine_prevista.format('YYYY-MM-DD') : null,
      };

      if (currentCantiere) {
        await cantieriService.update(currentCantiere.id, dataToSend);
      } else {
        await cantieriService.create(dataToSend);
      }
      loadData();
      handleCloseDialog();
    } catch (error) {
      setError(error.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  const handleOpenDetailDialog = (cantiere) => {
    navigate(`/cantieri/${cantiere.id}`);
  };

  const handleOpenDeleteDialog = (cantiere) => {
    setCantiereToDelete(cantiere);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCantiereToDelete(null);
  };

  const handleDelete = async () => {
    try {
      await cantieriService.delete(cantiereToDelete.id);
      loadData();
      handleCloseDeleteDialog();
    } catch (error) {
      setError('Errore nell\'eliminazione del cantiere');
    }
  };

  const handleOpenDirections = (cantiere) => {
    const indirizzo = `${cantiere.indirizzo}, ${cantiere.cap} ${cantiere.citta}, ${cantiere.provincia}`;
    const mapsUrl = `https://maps.google.com/maps?daddr=${encodeURIComponent(indirizzo)}`;
    window.open(mapsUrl, '_blank');
  };

  const getStatusColor = (stato) => {
    switch (stato) {
      case 'attivo':
        return { bg: '#dcfce7', color: '#166534' };
      case 'completato':
        return { bg: '#dbeafe', color: '#1e40af' };
      case 'sospeso':
        return { bg: '#fef3c7', color: '#92400e' };
      case 'annullato':
        return { bg: '#fef2f2', color: '#991b1b' };
      default:
        return { bg: '#f3f4f6', color: '#6b7280' };
    }
  };

  // Funzione per ottenere le iniziali

  // Statistiche
  const cantieriAttivi = cantieri.filter(c => c.stato === 'attivo').length;
  const cantieriCompletati = cantieri.filter(c => c.stato === 'completato').length;
  const totaleInterventi = cantieri.reduce((acc, c) => acc + (c.numero_interventi || 0), 0);

  // Loading skeleton
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <Box sx={{ bgcolor: '#1a1a1a', pt: 4, pb: 8, px: 3 }}>
          <Container maxWidth="xl">
            <Skeleton variant="text" width={250} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
            <Skeleton variant="text" width={350} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          </Container>
        </Box>
        <Container maxWidth="xl" sx={{ mt: -5 }}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rounded" height={60} sx={{ mb: 2, borderRadius: 2 }} />
            ))}
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Header scuro con statistiche */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          pt: { xs: 3, sm: 4 },
          pb: { xs: 12, sm: 14 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Pattern decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Accento rosso */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(229,0,0,0.3) 0%, transparent 70%)',
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Titolo e pulsante */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 4,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 800,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  mb: 0.5,
                }}
              >
                Gestione Cantieri
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.7)' }}
              >
                Monitora e gestisci tutti i tuoi cantieri
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: '#e50000',
                color: 'white',
                px: 3,
                py: 1.25,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
                '&:hover': {
                  bgcolor: '#c50000',
                  boxShadow: '0 6px 20px rgba(229,0,0,0.5)',
                },
              }}
            >
              Nuovo Cantiere
            </Button>
          </Box>

          {/* Statistiche */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <StatBox
              icon={BuildIcon}
              value={cantieri.length}
              label="Cantieri Totali"
              color="rgba(229,0,0,0.3)"
            />
            <StatBox
              icon={CheckCircleIcon}
              value={cantieriAttivi}
              label="Attivi"
              color="rgba(16,185,129,0.3)"
            />
            <StatBox
              icon={ScheduleIcon}
              value={cantieriCompletati}
              label="Completati"
              color="rgba(59,130,246,0.3)"
            />
            <StatBox
              icon={AssignmentIcon}
              value={totaleInterventi}
              label="Interventi Totali"
              color="rgba(245,158,11,0.3)"
            />
          </Box>
        </Container>
      </Box>

      {/* Contenuto principale */}
      <Container maxWidth="xl" sx={{ mt: { xs: -8, sm: -10 }, pb: 4, position: 'relative', zIndex: 2 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          {/* Barra di ricerca */}
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: '#fafafa',
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Cerca per codice, nome, città, indirizzo o cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: 500,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'white',
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: '#e5e7eb',
                  },
                  '&:hover fieldset': {
                    borderColor: '#d1d5db',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#e50000',
                    borderWidth: 1,
                  },
                },
              }}
            />
          </Box>

          {/* Vista Mobile - Cards */}
          {isMobile ? (
            <Box sx={{ p: 2 }}>
              {filteredCantieri.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <BuildIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} color="text.secondary">
                    Nessun cantiere trovato
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm ? 'Prova con altri termini di ricerca' : 'Inizia aggiungendo il tuo primo cantiere'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {paginatedCantieri.map((cantiere) => {
                    const statusColor = getStatusColor(cantiere.stato);
                    return (
                      <Paper
                        key={cantiere.id}
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#e50000',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          },
                        }}
                      >
                        {/* Header card */}
                        <Box
                          onClick={() => handleOpenDetailDialog(cantiere)}
                          sx={{ cursor: 'pointer', mb: 2 }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#e50000',
                                  fontWeight: 700,
                                  fontFamily: 'monospace',
                                }}
                              >
                                {cantiere.codice}
                              </Typography>
                              <Typography variant="subtitle1" fontWeight={700}>
                                {cantiere.nome}
                              </Typography>
                            </Box>
                            <Chip
                              label={cantiere.stato}
                              size="small"
                              sx={{
                                bgcolor: statusColor.bg,
                                color: statusColor.color,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                textTransform: 'capitalize',
                              }}
                            />
                          </Box>
                        </Box>

                        {/* Info */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {cantiere.cliente}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {cantiere.citta}{cantiere.provincia ? ` (${cantiere.provincia})` : ''}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Chips info */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                          {(cantiere.distanza_stradale_km || cantiere.distanza_sede_km) && (
                            <Chip
                              icon={<LocationIcon sx={{ fontSize: 14 }} />}
                              /* La lista restituisce solo distanza_sede_km (linea d'aria):
                                 la distanza stradale richiede una chiamata di routing e
                                 arriva solo nel dettaglio. Prima si leggeva unicamente
                                 distanza_stradale_km, quindi qui usciva sempre vuoto. */
                              label={
                                cantiere.distanza_stradale_km
                                  ? `${cantiere.distanza_stradale_km} km`
                                  : `~${Number(cantiere.distanza_sede_km).toFixed(1)} km`
                              }
                              title={
                                cantiere.distanza_stradale_km
                                  ? 'Distanza stradale'
                                  : "Distanza in linea d'aria"
                              }
                              size="small"
                              sx={{
                                bgcolor: '#f3f4f6',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            />
                          )}
                          {cantiere.tempo_viaggio_minuti && (
                            <Chip
                              icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                              label={`${cantiere.tempo_viaggio_minuti} min`}
                              size="small"
                              sx={{
                                bgcolor: '#f3f4f6',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            />
                          )}
                          <Chip
                            icon={<AssignmentIcon sx={{ fontSize: 14 }} />}
                            label={`${cantiere.numero_interventi || 0} interventi`}
                            size="small"
                            sx={{
                              bgcolor: '#dbeafe',
                              color: '#1e40af',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                        </Box>

                        {/* Azioni */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title="Visualizza">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDetailDialog(cantiere)}
                              sx={{
                                color: '#3b82f6',
                                '&:hover': { bgcolor: '#eff6ff' },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Indicazioni">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDirections(cantiere)}
                              sx={{
                                color: '#10b981',
                                '&:hover': { bgcolor: '#ecfdf5' },
                              }}
                            >
                              <DirectionsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Elimina">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteDialog(cantiere)}
                              sx={{
                                color: '#dc2626',
                                '&:hover': { bgcolor: '#fef2f2' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Box>
          ) : (
            /* Vista Desktop - Tabella */
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fafafa' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#374151', py: 2 }}>Cantiere</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#374151', py: 2 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#374151', py: 2 }}>Località</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#374151', py: 2 }}>Stato</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#374151', py: 2 }} align="center">Distanza</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#374151', py: 2 }} align="center">Interventi</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#374151', py: 2 }} align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCantieri.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 8, textAlign: 'center' }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: '#f3f4f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                          }}
                        >
                          <BuildIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
                        </Box>
                        <Typography variant="h6" fontWeight={600} color="text.secondary">
                          Nessun cantiere trovato
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm ? 'Prova con altri termini di ricerca' : 'Inizia aggiungendo il tuo primo cantiere'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedCantieri.map((cantiere) => {
                      const statusColor = getStatusColor(cantiere.stato);
                      return (
                        <TableRow
                          key={cantiere.id}
                          sx={{
                            '&:hover': { bgcolor: '#fafafa' },
                            transition: 'background-color 0.2s',
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: '#1a1a1a',
                                  fontWeight: 700,
                                  fontSize: '0.75rem',
                                }}
                              >
                                {iniziali(cantiere.nome)}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#e50000',
                                    fontWeight: 700,
                                    fontFamily: 'monospace',
                                    display: 'block',
                                  }}
                                >
                                  {cantiere.codice}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {cantiere.nome}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{cantiere.cliente}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {cantiere.citta}
                                {cantiere.provincia && (
                                  <Typography component="span" variant="caption" color="text.secondary">
                                    {' '}({cantiere.provincia})
                                  </Typography>
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={cantiere.stato}
                              size="small"
                              sx={{
                                bgcolor: statusColor.bg,
                                color: statusColor.color,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                textTransform: 'capitalize',
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {cantiere.distanza_stradale_km || cantiere.distanza_sede_km ? (
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {cantiere.distanza_stradale_km
                                    ? `${cantiere.distanza_stradale_km} km`
                                    : `~${Number(cantiere.distanza_sede_km).toFixed(1)} km`}
                                </Typography>
                                {cantiere.tempo_viaggio_minuti ? (
                                  <Typography variant="caption" color="text.secondary">
                                    {cantiere.tempo_viaggio_minuti} min
                                  </Typography>
                                ) : (
                                  !cantiere.distanza_stradale_km && (
                                    <Typography variant="caption" color="text.secondary">
                                      in linea d'aria
                                    </Typography>
                                  )
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" title="Cantiere senza coordinate">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={cantiere.numero_interventi || 0}
                              size="small"
                              sx={{
                                bgcolor: (cantiere.numero_interventi || 0) > 0 ? '#dbeafe' : '#f3f4f6',
                                color: (cantiere.numero_interventi || 0) > 0 ? '#1e40af' : '#6b7280',
                                fontWeight: 700,
                                minWidth: 32,
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Tooltip title="Visualizza dettagli">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDetailDialog(cantiere)}
                                  sx={{
                                    color: '#3b82f6',
                                    '&:hover': { bgcolor: '#eff6ff' },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Indicazioni stradali">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDirections(cantiere)}
                                  sx={{
                                    color: '#10b981',
                                    '&:hover': { bgcolor: '#ecfdf5' },
                                  }}
                                >
                                  <DirectionsIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Elimina">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDeleteDialog(cantiere)}
                                  sx={{
                                    color: '#dc2626',
                                    '&:hover': { bgcolor: '#fef2f2' },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Paginazione */}
          {filteredCantieri.length > 0 && (
            <TablePagination
              component="div"
              count={filteredCantieri.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Righe per pagina:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fafafa',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.875rem',
                },
              }}
            />
          )}
        </Paper>
      </Container>

      {/* Dialog Nuovo/Modifica Cantiere */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: isMobile ? 0 : 3,
            border: isMobile ? 'none' : '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: 2,
            px: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: currentCantiere ? '#f3f4f6' : '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {currentCantiere ? (
                <EditIcon sx={{ color: '#6b7280' }} />
              ) : (
                <AddIcon sx={{ color: '#dc2626' }} />
              )}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {currentCantiere ? 'Modifica Cantiere' : 'Nuovo Cantiere'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {currentCantiere ? 'Aggiorna i dati del cantiere' : 'Inserisci i dati del nuovo cantiere'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Dati Principali */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2 }}>
                DATI PRINCIPALI
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="codice"
                label="Codice Cantiere"
                fullWidth
                required
                value={formData.codice}
                onChange={handleInputChange}
                placeholder="es. CANT-001"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="nome"
                label="Nome Cantiere"
                fullWidth
                required
                value={formData.nome}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={clienti}
                getOptionLabel={(option) => option.ragione_sociale || ''}
                value={clienti.find(c => c.id === formData.cliente_id) || null}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    cliente_id: newValue ? newValue.id : '',
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente"
                    required
                    placeholder="Cerca cliente..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="Nessun cliente trovato"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                name="stato"
                label="Stato"
                fullWidth
                required
                value={formData.stato}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                <MenuItem value="attivo">Attivo</MenuItem>
                <MenuItem value="sospeso">Sospeso</MenuItem>
                <MenuItem value="completato">Completato</MenuItem>
                <MenuItem value="annullato">Annullato</MenuItem>
              </TextField>
            </Grid>

            {/* Indirizzo */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                INDIRIZZO
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="indirizzo"
                label="Indirizzo"
                fullWidth
                required
                value={formData.indirizzo}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                name="citta"
                label="Città"
                fullWidth
                required
                value={formData.citta}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                name="cap"
                label="CAP"
                fullWidth
                required
                value={formData.cap}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                name="provincia"
                label="Provincia"
                fullWidth
                required
                value={formData.provincia}
                onChange={handleInputChange}
                inputProps={{ maxLength: 2 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Date */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                DATE
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Data Inizio"
                value={formData.data_inizio}
                onChange={handleDateChange('data_inizio')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Data Fine Prevista"
                value={formData.data_fine_prevista}
                onChange={handleDateChange('data_fine_prevista')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                  },
                }}
              />
            </Grid>

            {/* Referente */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                REFERENTE CANTIERE
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="referente_cantiere"
                label="Nome Referente"
                fullWidth
                value={formData.referente_cantiere}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="telefono_referente"
                label="Telefono Referente"
                fullWidth
                value={formData.telefono_referente}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="email_referente"
                label="Email Referente"
                fullWidth
                type="email"
                value={formData.email_referente}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Note */}
            <Grid item xs={12}>
              <TextField
                name="note"
                label="Note"
                fullWidth
                multiline
                rows={3}
                value={formData.note}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: '#6b7280',
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={currentCantiere ? <CheckCircleIcon /> : <AddIcon />}
            sx={{
              bgcolor: '#e50000',
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#c50000' },
            }}
          >
            {currentCantiere ? 'Salva Modifiche' : 'Crea Cantiere'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <WarningIcon sx={{ color: '#dc2626', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Conferma Eliminazione
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Questa azione non può essere annullata
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Sei sicuro di voler eliminare il cantiere <strong>"{cantiereToDelete?.nome}"</strong>?
          </Typography>
          <Alert
            severity="error"
            icon={<WarningIcon />}
            sx={{
              borderRadius: 2,
              bgcolor: '#fef2f2',
              border: '1px solid #fecaca',
            }}
          >
            Questa azione eliminerà anche tutti gli interventi associati a questo cantiere.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{
              color: '#6b7280',
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Elimina Cantiere
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cantieri;
